import { useAuth } from '@opendoor/auth-fe/context';
import { useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowserAllowPartialData } from 'components/api/zeusBrowserClient';
import { captureError } from 'helpers/captureError';

import { HubPurchasePageDocument } from './__generated__/hub-purchase-page.zeus';
import type {
  HubPurchasePageQuery,
  HubPurchasePageQueryVariables,
} from './__generated__/hub-purchase-page.zeus';

export type HubPurchasePage = HubPurchasePageQuery['customerAccount']['buyer']['purchaseOffers'];

type PurchasePageOptions = {
  /** Tab, search and continuation to read. */
  variables: HubPurchasePageQueryVariables;
  /** Pause the read while its filters are changing. */
  enabled: boolean;
  /** Refetch cached data on mount by default; the table disables this to share its parent’s initial read. */
  refetchOnMount?: boolean;
  retainPreviousPage?: boolean;
};

export function useHubPurchasePage({
  variables,
  enabled,
  refetchOnMount = true,
  retainPreviousPage = true,
}: PurchasePageOptions) {
  const { authentication } = useAuth();
  const customerUuid =
    authentication.state === 'authenticated' ? authentication.user.customerUuid : null;
  return useQuery<{ page: HubPurchasePage; cursor: string | null }>({
    queryKey: [
      'hub',
      'purchase-page',
      customerUuid,
      variables.segment,
      variables.search ?? '',
      variables.after ?? null,
      variables.includeHomeSummary === true,
    ],
    placeholderData: (previousData, previousQuery) =>
      retainPreviousPage &&
      previousQuery?.queryKey[2] === customerUuid &&
      previousQuery.queryKey[3] === variables.segment &&
      previousQuery.queryKey[4] === (variables.search ?? '')
        ? previousData
        : undefined,
    enabled: customerUuid !== null && enabled,
    refetchOnMount,
    retry: false,
    meta: { captureErrorContext: 'HubPurchasePage' },
    queryFn: async ({ signal }) => {
      const result = await requestZeusFromBrowserAllowPartialData({
        document: HubPurchasePageDocument,
        variables,
        signal,
      });
      for (const error of result.errors) captureError(error, 'HubPurchasePage');
      const page = result.data?.customerAccount.buyer.purchaseOffers;
      if (!page) throw result.errors[0] ?? new Error('Purchase page unavailable');
      return { page, cursor: variables.after ?? null };
    },
  });
}
